<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { MoonIcon, Sun2Icon } from '@solar-icons/vue/linear'
import {
  SEGMENTED_SCALE,
  SEGMENTED_THUMB,
  SEGMENTED_THUMB_FACE,
} from '../AppSegmentedControl/segmented-control-scale'

const props = defineProps<{
  /** The theme on screen now. */
  theme: 'light' | 'dark'
}>()

const emit = defineEmits<{
  'update:theme': [value: 'light' | 'dark']
}>()

const { t } = useI18n()

const scale = SEGMENTED_SCALE.sm
const isDark = computed(() => props.theme === 'dark')

function flip(): void {
  emit('update:theme', isDark.value ? 'light' : 'dark')
}
</script>

<template>
  <button
    type="button"
    role="switch"
    :aria-checked="isDark"
    :aria-label="t('shell.darkTheme')"
    :class="[
      'inline-flex cursor-pointer select-none bg-bg-wash outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-blue',
      scale.rail,
    ]"
    @click="flip"
  >
    <span class="relative grid auto-cols-fr grid-flow-col">
      <span
        aria-hidden="true"
        :class="[
          SEGMENTED_THUMB,
          scale.thumb,
          'w-1/2',
          isDark ? 'translate-x-full' : 'translate-x-0',
        ]"
      >
        <span :class="[SEGMENTED_THUMB_FACE, scale.thumb]" />
      </span>
      <span
        :class="[
          'icon-sm relative z-10 box-content flex items-center justify-center transition-colors duration-base ease-soft',
          scale.iconBox,
          scale.height,
          scale.cellRadius,
          isDark ? 'text-text-tertiary' : 'text-text-primary',
        ]"
      >
        <Sun2Icon />
      </span>
      <span
        :class="[
          'icon-sm relative z-10 box-content flex items-center justify-center transition-colors duration-base ease-soft',
          scale.iconBox,
          scale.height,
          scale.cellRadius,
          isDark ? 'text-text-primary' : 'text-text-tertiary',
        ]"
      >
        <MoonIcon />
      </span>
    </span>
  </button>
</template>
